import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '@reveni-io/reveni-ui'

import useWebsocketAction from '@/common/hooks/useWebsocketAction/useWebsocketAction'
import configurationServices from '@/configuration/services/services'

import { getInstallingWebsocketUrl } from './helpers'

const useIntegrations = ({ storeId, setToastInfo }) => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const {
    data: integrations,
    loading: gettingIntegrations,
    fetchData: refetchIntegrations,
  } = useFetch(() => configurationServices.getIntegrations({ storeId }), {
    doInitialRequest: true,
  })

  const { fetchData: toggleIntegration, loading: togglingIntegration } = useFetch(
    configurationServices.toggleIntegration,
    {
      doInitialRequest: false,
    }
  )

  const { fetchData: uninstallIntegration, loading: uninstallingIntegration } = useFetch(
    configurationServices.uninstallIntegration,
    {
      doInitialRequest: false,
    }
  )

  const onInstallationFinished = () => refetchIntegrations()

  useWebsocketAction({
    websocketUrl: getInstallingWebsocketUrl(integrations),
    onComplete: onInstallationFinished,
    onError: onInstallationFinished,
  })

  const filteredIntegrations = useMemo(() => {
    if (!searchQuery || !integrations?.results) {
      return integrations?.results
    }
    const query = searchQuery.toLowerCase()
    return integrations?.results.filter(integration => integration.name.toLowerCase().includes(query))
  }, [searchQuery, integrations])

  const onSearchChange = searchQuery => {
    setSearchQuery(searchQuery)
  }

  const onToggle = async (integration, checked) => {
    try {
      await toggleIntegration({
        storeId,
        integrationId: integration.id,
        value: checked ? 'enable' : 'disable',
      })
      setToastInfo({ show: true, message: t('Modificado correctamente'), type: 'success' })
      refetchIntegrations()
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const onUninstall = async integration => {
    try {
      await uninstallIntegration({
        storeId,
        integrationId: integration.id,
      })
      setToastInfo({ show: true, message: t('Desinstalado correctamente'), type: 'success' })
      refetchIntegrations()
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  return {
    integrations: filteredIntegrations || [],
    gettingIntegrations,
    refetchIntegrations,
    searchQuery,
    onSearchChange,
    onToggle,
    togglingIntegration,
    onUninstall,
    uninstallingIntegration,
  }
}

export default useIntegrations
